/**
 * Glass-Box-Single-Source für Rolle × Berechtigung.
 *
 * Jeder Role-Check (Server-Middleware, Frontend-Route-Gate, UI-Element-Toggle)
 * MUSS auf einen Eintrag in PERMISSIONS verweisen. So gibt es genau EINE
 * Wahrheit pro Gate — und der Drift-Detector im Werkstatt-Zweig "Zugriffs-
 * Matrix" kann mechanisch prüfen, ob alle Stellen konsistent sind.
 *
 * Warum: am 2026-05-18 wurde die Migration 033 (Role "developer") in 5 Files
 * separat eingebaut. AppShell + KanbanPage waren konsistent, SettingsPage
 * nicht — und das wurde erst durch User-Beobachtung entdeckt. Mit Registry +
 * Drift-Detector wäre die Inkonsistenz beim Build/Render sichtbar geworden.
 */

export type Role = "user" | "admin" | "developer";

export interface PermissionRow {
  /** Stabile semantische ID. Wird als data-perm="..." auf UI-Elemente gehängt
   *  und im Drift-Detector zum Matchen verwendet. */
  id: string;
  /** Welche Art Gate ist das? Bestimmt wie der Drift-Detector probt. */
  scope: "route" | "api" | "ui";
  /** Pfad/Selektor. Route: "/werkstatt/*"; api: "GET /api/users"; ui: "data-perm-Wert". */
  path: string;
  /** Welche Rollen dürfen — Disjunktion (mind. eine erfüllt). */
  required_roles: Role[];
  /** Kurztext für die Matrix-Anzeige. */
  description: string;
  /** Wo im Code wird das Gate aktuell durchgesetzt? File-Pfade als Pointer. */
  enforced_in: string[];
  /** Warum dieses Gate — institutionelles Gedächtnis. */
  rationale: string;
}

/** Reihenfolge für die Matrix-Spalten. */
export const ROLE_ORDER: Role[] = ["user", "admin", "developer"];

export const ROLE_LABELS: Record<Role, string> = {
  user: "User (Lohnverrechner)",
  admin: "Admin (Office-Lead)",
  developer: "Developer (Glass-Box-Internals)",
};

/**
 * Rollen-Vererbung. Migration 033: developer hat alle admin-Rechte, admin alle
 * user-Rechte. Beim Check immer roleSatisfies() verwenden — niemals strict
 * equality (`role === "admin"`), das ist genau der Drift-Bug von 2026-05-18.
 */
export const ROLE_HIERARCHY: Record<Role, Role[]> = {
  developer: ["developer", "admin", "user"],
  admin: ["admin", "user"],
  user: ["user"],
};

export function roleSatisfies(actual: Role, required: Role[]): boolean {
  const inherited = ROLE_HIERARCHY[actual] ?? [actual];
  return required.some((r) => inherited.includes(r));
}

/** Convenience: hat der User Zugriff auf das benannte Gate? */
export function hasPermission(actual: Role | undefined | null, permId: string): boolean {
  if (!actual) return false;
  const perm = PERMISSIONS.find((p) => p.id === permId);
  if (!perm) return false;
  return roleSatisfies(actual, perm.required_roles);
}

/**
 * Die kanonische Liste aller Role-Gates. Wenn du ein neues Gate einbaust:
 *   1. Eintrag hier hinzufügen (mit enforced_in + rationale).
 *   2. Im Code roleSatisfies(user.role, perm.required_roles) ODER
 *      hasPermission(user.role, "perm-id") verwenden — niemals strict equality.
 *   3. data-perm="<id>" auf UI-Elemente, damit der DOM-Scanner sie findet.
 */
export const PERMISSIONS: PermissionRow[] = [
  {
    id: "werkstatt-route",
    scope: "route",
    path: "/werkstatt/*",
    required_roles: ["developer"],
    description: "Pipeline-Werkstatt — Glass-Box-Doku + Step-UUIDs + Dev-Tools",
    enforced_in: [
      "frontend/src/App.tsx:protectDeveloper",
      "frontend/src/components/AppShell.tsx (nav-link)",
    ],
    rationale: "Technische Internals — für reine Lohnverrechner irrelevant.",
  },
  {
    id: "kanban-werkstatt-link",
    scope: "ui",
    path: "kanban-werkstatt-link",
    required_roles: ["developer"],
    description: "Werkstatt-Direkt-Link auf jeder Kanban-Karte",
    enforced_in: ["frontend/src/pages/KanbanPage.tsx (KanbanCard isDeveloper)"],
    rationale: "Sprung von Karte direkt in die passende Phase — nur Developer.",
  },
  {
    id: "maintenance-bypass",
    scope: "route",
    path: "* (bei maintenance.active=true)",
    required_roles: ["developer"],
    description: "Wartungsmodus-Bypass — App trotz aktivem Wartungsfenster nutzen",
    enforced_in: [
      "hub/src/app.ts (Maintenance-Middleware, rollenbewusst seit 2026-06-08)",
    ],
    rationale:
      "Bei aktivem Wartungsfenster sehen user + admin (inkl. Kundin) die Wartungsseite (HTTP 503); nur developer arbeitet dahinter normal weiter. Ausnahmen für alle Rollen: /login, /api/auth/*, /api/me, Assets, /health — damit ein developer sich auch frisch einloggen kann. Rolle aus Session-Cookie, fail-closed bei Fehler.",
  },
  {
    id: "users-api",
    scope: "api",
    path: "/api/users/*",
    required_roles: ["admin", "developer"],
    description: "User-Management (Liste, Einladen, Rolle ändern, Löschen)",
    enforced_in: [
      "hub/src/routes/users.ts (requireAdmin)",
      "frontend/src/pages/SettingsPage.tsx (tab Benutzer)",
    ],
    rationale: "Account-Verwaltung gehört in die Office-Lead-Hand.",
  },
  {
    id: "admin-api",
    scope: "api",
    path: "/api/admin/*",
    required_roles: ["admin", "developer"],
    description: "Admin-Routen (Health, Settings, RAG, Re-OCR-Trigger)",
    enforced_in: [
      "hub/src/routes/admin.ts (requireAdmin)",
      "frontend/src/pages/SettingsPage.tsx (tab Admin)",
    ],
    rationale: "Destruktive / globale Operations.",
  },
  {
    id: "qa-api",
    scope: "api",
    path: "/api/qa/*",
    required_roles: ["admin", "developer"],
    description: "QA-Excel-Export + Eval-Runs",
    enforced_in: [
      "hub/src/routes/qa.ts (requireAdmin)",
      "frontend/src/pages/SettingsPage.tsx (tab OCR-Test)",
    ],
    rationale: "OCR-Evaluation ist Developer/Admin-Werkzeug.",
  },
  {
    id: "test-protocols-api",
    scope: "api",
    path: "/api/admin/test-protocols/*",
    required_roles: ["admin", "developer"],
    description: "Test-Protokoll-Verwaltung",
    enforced_in: [
      "hub/src/routes/test-protocols.ts (requireAdmin)",
      "frontend/src/components/AppShell.tsx (Test-Protokoll-Nav)",
    ],
    rationale: "QA-Artefakte für Owner-Reviews.",
  },
  {
    id: "settings-ocr-rules",
    scope: "ui",
    path: "settings-ocr-rules",
    required_roles: ["admin", "developer"],
    description: "Einstellungen → OCR-Regeln",
    enforced_in: ["frontend/src/pages/SettingsPage.tsx (tab ocr-rules)"],
    rationale: "Engine-Tuning ist Developer/Admin-Aufgabe.",
  },
  {
    id: "settings-ocr-thresholds",
    scope: "ui",
    path: "settings-ocr-thresholds",
    required_roles: ["admin", "developer"],
    description: "Einstellungen → OCR-Schwellen",
    enforced_in: ["frontend/src/pages/SettingsPage.tsx (tab ocr-thresholds)"],
    rationale: "Konfidenz-Schwellen tunen.",
  },
  {
    id: "settings-ocr-test",
    scope: "ui",
    path: "settings-ocr-test",
    required_roles: ["admin", "developer"],
    description: "Einstellungen → OCR-Test (Eval gegen Ground-Truth)",
    enforced_in: ["frontend/src/pages/SettingsPage.tsx (tab ocr-test)"],
    rationale: "Eval-Werkzeug — kann Re-OCR triggern.",
  },
  {
    id: "settings-users",
    scope: "ui",
    path: "settings-users",
    required_roles: ["admin", "developer"],
    description: "Einstellungen → Benutzer",
    enforced_in: ["frontend/src/pages/SettingsPage.tsx (tab users)"],
    rationale: "Spiegelt users-api auf der UI-Seite.",
  },
  {
    id: "settings-admin",
    scope: "ui",
    path: "settings-admin",
    required_roles: ["admin", "developer"],
    description: "Einstellungen → Admin",
    enforced_in: ["frontend/src/pages/SettingsPage.tsx (tab admin)"],
    rationale: "Spiegelt admin-api auf der UI-Seite.",
  },
  {
    id: "test-protocols-nav",
    scope: "ui",
    path: "test-protocols-nav",
    required_roles: ["admin", "developer"],
    description: "Top-Nav → Test-Protokoll",
    enforced_in: ["frontend/src/components/AppShell.tsx (isAdmin Link)"],
    rationale: "Quick-Access zu Test-Protokollen.",
  },
];

/**
 * Live-Probe-Targets für den Server-Gate-Step im Werkstatt-Zweig.
 * Jeder Eintrag verweist auf eine permission_id und einen tatsächlich
 * ausführbaren HTTP-Call (read-only, idempotent).
 */
export interface ProbeTarget {
  perm_id: string;
  method: "GET";
  path: string;
  /** Erwartetes Verhalten für die jeweilige Rolle. */
  expected_status: Partial<Record<Role, number>>;
}

export const PROBE_TARGETS: ProbeTarget[] = [
  {
    perm_id: "users-api",
    method: "GET",
    path: "/api/users",
    expected_status: { user: 403, admin: 200, developer: 200 },
  },
  {
    perm_id: "admin-api",
    method: "GET",
    path: "/api/admin/health",
    expected_status: { user: 403, admin: 200, developer: 200 },
  },
  {
    perm_id: "test-protocols-api",
    method: "GET",
    path: "/api/admin/test-protocols",
    expected_status: { user: 403, admin: 200, developer: 200 },
  },
];
